import flattenDeep from 'lodash/flattenDeep';
import last from 'lodash/last';
import { useCallback } from 'react';
import useSWRInfinite from 'swr/infinite';
import useBus from 'use-bus';
import {
  deleteComment,
  likeComment,
  unlikeComment,
} from '~/app/_repositories/comment-service';
import { useTellerSession } from '~/common/hooks/use-auth/use-teller-session';
import { sendToFirebaseAnalytics } from '~/common/hooks/use-firebase-analytics';
import type { StoryComment } from '~/common/types/story-comment';
import {
  createLikeOrUnlikeOptimisticData,
  getPostReplyKey,
} from '@app/(default-layout)/s/[storyId]/_utils/comment-util';
import { useSystemNotification } from '@app/_hooks/use-system-notification';
import { getCommentReplyListRequest } from './requests/get-reply-comments-list';

type Params = {
  storyId: string;
  parentCommentId: string;
};

const key = 'get-reply-comments-detail';
const PAGINATION_LIMIT = 10;

type SWRArguments = {
  key: string;
  storyId: string;
  parentCommentId: string;
  cursor: string | undefined;
};

export const useLocalState = ({ storyId, parentCommentId }: Params) => {
  const { session } = useTellerSession();
  const tellerUser = session?.tellerUser;
  const { notify } = useSystemNotification();

  const getKey = useCallback(
    (_, prevData): SWRArguments => {
      if (!prevData) {
        return {
          key,
          storyId,
          parentCommentId,
          cursor: undefined,
        };
      }
      const nextCursor = prevData?.nextCursor;

      return {
        key,
        storyId,
        parentCommentId,
        cursor: nextCursor,
      };
    },
    [parentCommentId, storyId]
  );

  const {
    data: commentsResult,
    isLoading,
    error,
    size,
    setSize,
    mutate,
  } = useSWRInfinite(getKey, ({ storyId, parentCommentId, cursor }) => {
    return getCommentReplyListRequest({
      storyId,
      commentId: parentCommentId,
      cursor,
      limit: PAGINATION_LIMIT,
    });
  });

  const hasNext = last(commentsResult)?.hasNext ?? false;

  const comments = flattenDeep(
    commentsResult?.map((result) => result.comments) ?? []
  );

  const isEmpty =
    typeof commentsResult !== 'undefined' && comments.length === 0;

  const handleLoadMore = () => {
    setSize(size + 1);
  };

  const handleOnClickLike = useCallback(
    (comment: StoryComment) => {
      const resultData = createLikeOrUnlikeOptimisticData(
        comment,
        commentsResult
      );

      if (comment.likedByMe) {
        mutate(
          unlikeComment(comment.id).then(() => resultData),
          {
            optimisticData: resultData,
          }
        );
      } else {
        mutate(
          likeComment(comment.id).then(() => resultData),
          {
            optimisticData: resultData,
          }
        );

        // send analytics
        sendToFirebaseAnalytics('story_add_like_to_comment', {
          story_id: storyId,
        });
      }
    },
    [commentsResult, mutate, storyId]
  );

  // Delete Comment
  const handleOnDelete = useCallback(
    async (comment: StoryComment) => {
      if (
        typeof window !== 'undefined' &&
        window.confirm('コメントを削除しますか？')
      ) {
        const resultData = commentsResult?.map((result) => {
          return {
            ...result,
            comments: result.comments.filter((c) => c.id !== comment.id),
          };
        });

        await mutate(
          deleteComment(comment.id).then(() => resultData),
          {
            optimisticData: resultData,
          }
        );

        notify('コメントを削除しました', 'info', {
          position: 'top-center',
        });

        // send analytics
        sendToFirebaseAnalytics('story_delete_comment', {
          story_id: storyId,
        });
      }
    },
    [commentsResult, mutate, notify, storyId]
  );

  // handle post comment
  const handleOnPostReply = useCallback(async () => {
    mutate();
  }, [mutate]);
  useBus(getPostReplyKey(parentCommentId), handleOnPostReply);

  return {
    tellerUser,
    comments: comments,
    isLoading: isLoading,
    isEmpty,
    error,
    hasNext,
    handleLoadMore,
    handleOnClickLike,
    handleOnDelete,
  };
};
